import React from 'react'
import styled from 'styled-components'
import { 
  IconStar, 
  IconCrown, 
  IconDiamond, 
  IconShieldCheck,
  IconMedal,
  IconAward
} from '@tabler/icons-react'
import { useGroupFee } from 'app/hooks/adminUsers/groupFee'

const LevelContainer = styled.div<{ $level: number }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: ${props => {
    const hue = (props.$level * 40) % 360
    return `linear-gradient(135deg, hsla(${hue}, 70%, 50%, 0.1) 0%, hsla(${hue + 30}, 70%, 40%, 0.1) 100%)`
  }};
  border: 1px solid ${props => {
    const hue = (props.$level * 40) % 360
    return `hsla(${hue}, 70%, 50%, 0.25)`
  }};
  border-radius: 12px;
  position: relative;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.02);
    box-shadow: ${props => {
      const hue = (props.$level * 40) % 360
      return `0 2px 8px hsla(${hue}, 70%, 50%, 0.2)`
    }};
  }
`

const LevelBadge = styled.div<{ $level: number }>`
  background: ${props => {
    const hue = (props.$level * 40) % 360
    return `linear-gradient(135deg, hsl(${hue}, 70%, 50%) 0%, hsl(${hue + 30}, 70%, 40%) 100%)`
  }};
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
`

const LevelText = styled.span`
  color: rgba(50, 50, 50, 0.85);
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  letter-spacing: 0.2px;
`

const IconWrapper = styled.div<{ $level: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => {
    const hue = (props.$level * 40) % 360
    return `hsl(${hue}, 70%, 50%)`
  }};
  
  svg {
    width: 14px;
    height: 14px;
  }
`

const CompactLevelBadge = styled.div<{ $level: number }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 5px;
  background: ${props => {
    const hue = (props.$level * 40) % 360
    return `linear-gradient(135deg, hsla(${hue}, 70%, 50%, 0.15) 0%, hsla(${hue + 30}, 70%, 40%, 0.15) 100%)`
  }};
  border: 1px solid ${props => {
    const hue = (props.$level * 40) % 360
    return `hsla(${hue}, 70%, 50%, 0.35)`
  }};
  border-radius: 10px;
  
  span {
    color: ${props => {
      const hue = (props.$level * 40) % 360
      return `hsl(${hue}, 70%, 45%)`
    }};
    font-size: 10px;
    font-weight: 600;
  }
  
  svg {
    width: 10px;
    height: 10px;
  }
`

interface ModernLevelIconProps {
  level: number
  compact?: boolean
  showIcon?: boolean
  showText?: boolean
}

const ModernLevelIcon: React.FC<ModernLevelIconProps> = ({ 
  level, 
  compact = false,
  showIcon = true,
  showText = true 
}) => {
  const { data } = useGroupFee()
  const levelInfo = data?.find((group: any) => group.level === level)
  const levelName = levelInfo?.name?.replace(/회원/gi, '') || `Level ${level}`
  
  const getLevelIcon = () => {
    if (level >= 9) return <IconCrown />
    if (level >= 7) return <IconDiamond />
    if (level >= 5) return <IconShieldCheck />
    if (level >= 3) return <IconMedal />
    if (level >= 1) return <IconAward />
    return <IconStar />
  }
  
  if (compact) {
    return (
      <CompactLevelBadge $level={level}>
        {showIcon && getLevelIcon()}
        <span>{level}</span>
      </CompactLevelBadge>
    )
  }
  
  return (
    <LevelContainer $level={level}>
      {showIcon && (
        <IconWrapper $level={level}>
          {getLevelIcon()}
        </IconWrapper>
      )}
      <LevelBadge $level={level}>
        {level}
      </LevelBadge>
      {showText && <LevelText>{levelName}</LevelText>}
    </LevelContainer>
  )
}

export default React.memo(ModernLevelIcon)